import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  InputAdornment,
  TextField,
  useMediaQuery,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';

import { popoverShadow } from 'theme/shadows';
import { useVisibleSpaceBelow } from '@hooks';

const FIELD_WIDTH = '26rem';

const LISTBOX_GAP = 16;
const FIELD_GAP_BELOW_APP_BAR = 8;

export const SearchField: React.FC<{
  value: string;
  placeholder: string;
  fullWidth?: boolean;
  suggestions?: { kind: string; value: string }[];
  onChange: (value: string) => void;
}> = ({ value, placeholder, fullWidth, suggestions = [], onChange }) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const isTouch = useMediaQuery('(hover: none)');
  const [open, setOpen] = useState(false);
  const fitAboveKeyboard = isTouch && open && suggestions.length > 0;

  useEffect(() => {
    const field = fieldRef.current;
    if (!fitAboveKeyboard || !field) return;

    field.scrollIntoView({ block: 'start' });
    const appBarBottom =
      document.querySelector('header')?.getBoundingClientRect().bottom ?? 0;
    const overlap = appBarBottom - field.getBoundingClientRect().top;
    if (overlap > 0) window.scrollBy(0, -(overlap + FIELD_GAP_BELOW_APP_BAR));
  }, [fitAboveKeyboard]);

  const spaceBelow = useVisibleSpaceBelow(fieldRef, fitAboveKeyboard);

  return (
    <Autocomplete
      ref={fieldRef}
      freeSolo
      disableClearable={false}
      options={suggestions}
      groupBy={(option) => option.kind}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.value
      }
      filterOptions={(all) => all}
      inputValue={value}
      onInputChange={(_, next, reason) => {
        if (reason !== 'reset' || next) onChange(next);
      }}
      onChange={(_, selected) =>
        onChange(
          typeof selected === 'string' ? selected : selected?.value ?? '',
        )
      }
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      ListboxProps={
        spaceBelow === undefined
          ? undefined
          : {
              style: { maxHeight: `min(20rem, ${spaceBelow - LISTBOX_GAP}px)` },
            }
      }
      slotProps={{
        paper: {
          sx: {
            boxShadow: popoverShadow,
            '& .MuiAutocomplete-listbox': {
              maxHeight: '20rem',
            },
            '& .MuiAutocomplete-groupLabel': {
              fontSize: '0.8rem',
              lineHeight: 1.4,
              py: 0.5,
              mb: 0,
            },
            '& .MuiAutocomplete-option': { fontSize: '0.875rem' },
          },
        },
      }}
      sx={{
        width: fullWidth ? '100%' : FIELD_WIDTH,
        maxWidth: '100%',
        // `&&&`: Autocomplete's own input-padding selectors outrank a plain `& .x`.
        '&&& .MuiOutlinedInput-root': {
          height: '2.5rem',
          gap: 1,
          py: 0,
          pl: 1.5,
          pr: 1,
          backgroundColor: 'common.white',
        },
        '&&& .MuiAutocomplete-input': { p: 0 },
        '& .MuiInputAdornment-root': { m: 0 },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'background.divider',
        },
        '&:hover .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
          { borderColor: 'background.dividerStrong' },
        '& input::placeholder': { color: 'text.lightGrey', opacity: 1 },
        '& .MuiAutocomplete-clearIndicator': {
          visibility: 'visible',
          width: '1.5rem',
          height: '1.5rem',
          p: 0,
          mr: 0,
          borderRadius: '4px',
          color: 'text.secondary',
          '& svg': { fontSize: '1rem' },
          '&:hover': {
            backgroundColor: 'highlight.neutral',
            color: 'text.primary',
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ fontSize: '1.25rem', color: 'text.secondary' }}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
